/**
 * bitsandbytes NF4/FP4 — the fourth packed family, and the one that fails
 * quietly.
 *
 * ## What goes wrong without this
 *
 * `unsloth/llama-3-8b-bnb-4bit`, layer 0 `mlp.down_proj`. The header says
 * `U8`, shape `[29360128, 1]`. Read naively that is 29 360 128 elements in
 * 29 360 128 bytes: **8.00 bits per weight**. The truth is 4.127.
 *
 * Two-fold wrong, not eightfold — which is what makes it worse than the cases
 * before it. An AWQ tensor reporting 32.00 is obviously broken and someone
 * files it. A 4-bit checkpoint reporting 8.00 just looks like a repo that
 * shipped int8, and nobody blinks (end-user report, 2026-08-10).
 *
 * ## Why the trick that rescued AWQ and GPTQ does not work here
 *
 * There, `scales` still carried `out_features` in its last dimension, so
 * `scales` plus `qweight` pinned the axis and the logical shape fell out. NF4
 * has no `scales` tensor at all, and the payload is not packed along an axis —
 * it is **flattened**. Both dimensions are gone from every header field:
 *
 *     ...down_proj.weight                              U8   [29360128, 1]
 *     ...down_proj.weight.absmax                       U8   [917504]
 *     ...down_proj.weight.nested_absmax                F32  [3584]
 *     ...down_proj.weight.nested_quant_map             F32  [256]
 *     ...down_proj.weight.quant_map                    F32  [16]
 *     ...down_proj.weight.quant_state.bitsandbytes__nf4 U8  [173]
 *
 * The logical shape survives only inside that last one, a 173-byte JSON blob
 * in the *payload* — `{"quant_type":"nf4","blocksize":64,"shape":[4096,14336],
 * …}`.
 *
 * ## …and why this module still needs no payload read
 *
 * Because the parameter count is **over-determined by the headers alone**.
 * NF4 packs two values per byte and quantizes `absmax` per block, so:
 *
 *     weight.numel x 2  ==  absmax.numel x blocksize
 *     58 720 256        ==  58 720 256                (measured)
 *
 * Two independent routes to the same number, from shapes we already hold. That
 * is what lets the map's budget line ship header-only and keeps "no weights
 * fetched" true (reviewer ruling, 2026-08-10). Reading the JSON is for the
 * logical *2-D shape* and for decode — neither of which the byte accounting
 * needs — and would be disclosed when it happens.
 *
 * The relation is used as a **cross-check**, not as a shortcut: `blocksize` is
 * derived from it and a non-integer means the two accounts disagree, which is
 * recorded rather than smoothed.
 *
 * ## Membership comes from the module path
 *
 * The anchor moves three ways across the families, which is why a literal name
 * list cannot group them:
 *
 *     AWQ   gate_proj   qweight, qzeros, scales      — no tensor called `weight`
 *     FP8   down_proj   weight, weight_scale, input_scale   — siblings
 *     NF4   down_proj   weight, weight.absmax, …     — **children of `weight`**
 *
 * So the rule here is the reviewer's: **the path claims membership, and
 * per-family construction interprets.** Everything under `<module>.weight.` is
 * in the group; nothing needs to be named in advance, so a bitsandbytes release
 * that adds a seventh sidecar is counted the day it appears rather than
 * silently dropped.
 *
 * Grouping by path is emphatically *not* folding by proximity: a group is only
 * built when the file states its own scheme (`quant_state.bitsandbytes__*`) and
 * the two parameter accounts agree.
 *
 * Pure: names and shapes in, groups out. No I/O.
 */

export interface Nf4TensorLike {
  readonly name: string;
  readonly dtype: string;
  readonly shape: readonly number[];
  readonly numel: number;
  readonly byteLength: number;
}

export interface Nf4Group {
  /** Logical name — the payload's own name, as for the other folders. */
  readonly prefix: string;
  readonly payloadName: string;
  /** Every physical tensor in the group, payload first. */
  readonly partNames: readonly string[];
  /** `nf4` or `fp4`, as the checkpoint itself spells it. */
  readonly quantType: string;
  /** 4 for both types this module accepts. */
  readonly bits: number;
  /** Elements per `absmax` entry, derived from the two accounts agreeing. */
  readonly groupSize: number;
  /** Weights in the logical matrix, before packing. */
  readonly params: number;
  /** Payload plus every sidecar. */
  readonly bytes: number;
  /** What the scheme actually costs. 4.127, not 4.000 and not 8.00. */
  readonly effectiveBpw: number;
  /** The payload alone — exactly `bits` by construction, so it is a check. */
  readonly payloadBpw: number;
}

/**
 * The suffix that names the scheme, and the scheme it names.
 *
 * `weight.quant_state.bitsandbytes__nf4` — the checkpoint states its own
 * format, which is a better anchor than inferring one from a `U8` payload.
 * `U8` is also int8, also a packed 4-bit anything, also a byte buffer.
 */
const QUANT_STATE = /\.quant_state\.bitsandbytes__(\w+)$/;

/**
 * The 4-bit bitsandbytes types: two values per byte.
 *
 * `int8` is deliberately absent. bitsandbytes' 8-bit path stores one value per
 * byte under a different sidecar vocabulary (`SCB`), so `numel x 2` would be
 * exactly twice wrong there — the same shape of error this module exists to
 * fix, pointing the other way. An unrecognised type is refused, not guessed.
 */
const FOUR_BIT_TYPES: ReadonlySet<string> = new Set(["nf4", "fp4"]);

/** Values packed per byte for the types above. */
const VALUES_PER_BYTE = 2;

/**
 * Find every bitsandbytes 4-bit group among these tensors.
 *
 * A group is built only when all of the following hold, and none of them is an
 * assumption:
 *
 * 1. some `<module>.weight.quant_state.bitsandbytes__<type>` exists, so the
 *    checkpoint has named its own scheme;
 * 2. `<type>` is one this module knows the packing of;
 * 3. `<module>.weight` exists and is a byte payload;
 * 4. `<module>.weight.absmax` exists;
 * 5. the two parameter accounts agree on a whole-number block size.
 *
 * Anything short of that is left ungrouped, which shows up as the unfolded
 * bytes it is rather than as a confident wrong number.
 */
export function findNf4Groups(tensors: readonly Nf4TensorLike[]): Nf4Group[] {
  const byName = new Map(tensors.map((t) => [t.name, t]));
  const groups: Nf4Group[] = [];

  for (const state of tensors) {
    const match = QUANT_STATE.exec(state.name);
    if (!match) continue;
    const quantType = match[1] as string;
    if (!FOUR_BIT_TYPES.has(quantType)) continue;

    // `<module>.weight`, by removing the suffix this tensor announced itself with.
    const payloadName = state.name.slice(0, -match[0].length);
    const payload = byName.get(payloadName);
    if (!payload) continue;
    // A byte buffer, which is what a packed 4-bit payload is stored as. Not a
    // guarantee of anything on its own — see `QUANT_STATE` — but a group whose
    // payload is F32 is not this scheme whatever it calls itself.
    if (payload.dtype !== "U8" && payload.dtype !== "I8") continue;

    const absmax = byName.get(`${payloadName}.absmax`);
    if (!absmax || absmax.numel <= 0) continue;

    /*
     * The over-determination, used as a check.
     *
     * `params` from the payload; `groupSize` from `absmax` having one entry per
     * block of them. If that division is not exact the two accounts of this
     * tensor disagree, and the honest response is to leave the group unformed
     * rather than to publish whichever number was computed first.
     */
    const params = payload.numel * VALUES_PER_BYTE;
    if (params % absmax.numel !== 0) continue;
    const groupSize = params / absmax.numel;
    if (!Number.isFinite(groupSize) || groupSize < 2) continue;

    /*
     * Membership by path: the payload plus everything under it.
     *
     * `startsWith(payloadName + ".")` rather than a list of known suffixes, so
     * a sidecar this build has never heard of is still counted. Its bytes are
     * real whatever it is called, and a byte left out of the numerator is the
     * whole failure being fixed here.
     */
    const partNames = [payloadName];
    let bytes = payload.byteLength;
    for (const t of tensors) {
      if (!t.name.startsWith(`${payloadName}.`)) continue;
      partNames.push(t.name);
      bytes += t.byteLength;
    }

    groups.push({
      prefix: payloadName,
      payloadName,
      partNames,
      quantType,
      bits: 8 / VALUES_PER_BYTE,
      groupSize,
      params,
      bytes,
      effectiveBpw: (bytes * 8) / params,
      payloadBpw: (payload.byteLength * 8) / params,
    });
  }

  return groups;
}
